import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// MUI
import {
  Container,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from '@mui/material';

import PricingControlsTable from '~/components/AdminPortal/PricingControls/SharedTable';
import OlcButton from '~/components/General/OlcButton';
import TruncatedText from '~/components/General/TruncatedText';
import useScreenWidth from '~/hooks/useScreenWidth';
import '../styles.scss';
import './styles.scss';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import ConfirmModal from '~/components/General/ConfirmModal';
import { ContactDetailsDeleteCIcon, TrashTemplateCIcon } from '~/assets/images';
import UpdatePricingModal from './UpdatePricingModal';
import {
  fetchAdminPricingTiersFiltersData,
  fetchAdminPricingTiersVersionsData,
  fetchAdminTieredPricingData,
  deleteAdminPricingTier,
  updateAdminPricingTiers,
} from '~/redux/actions/admin-pricing-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { MESSAGES } from '~/utils/message';

const TIER_WIDTH = 150;
const QTY_WIDTH = 150;
const FC_GLOBAL_WIDTH_STANDARD = 350;
const FC_GLOBAL_WIDTH = 250;
const FC_ACCOUNT_WIDTH = 200;
const FC_GP_WIDTH = 160;
const FC_VARIANCE_WIDTH = 150;
const STD_GLOBAL_WIDTH_STANDARD = 350;
const STD_GLOBAL_WIDTH = 200;
const STD_ACCOUNT_WIDTH = 200;
const STD_GP_WIDTH = 190;
const STD_VARIANCE_WIDTH = 150;

const getNumericFromCurrency = (val) => {
  const str = String(val ?? '');
  // Preserve negative sign
  const isNegative = str.trim().startsWith('-');
  const cleaned = str.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  const intPart = parts[0] || '';
  const decPart = parts[1] ? parts[1].replace(/[^0-9]/g, '').slice(0, 3) : '';
  const result = decPart ? `${intPart}.${decPart}` : intPart;
  return isNegative && result ? `-${result}` : result;
};

const toCurrencyString = (numericStr) => {
  const cleaned = getNumericFromCurrency(numericStr);
  return cleaned ? `$${cleaned}` : '';
};

// Format date to YYYY-MM-DD format for API payloads
const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // If it's already a string, extract just the date part
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    // Format Date object to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

const buildTierColumns = ({
  isEditing,
  draftByRowId,
  setDraftByRowId,
  setTierRows,
  apiData,
  calculateGPPercent,
  isStandard,
  isWideScreen,
}) => {
  // Determine if we should use first_class_forever instead of first_class
  const useFirstClassForever = apiData?.has_first_class_forever;
  const firstClassLabel = useFirstClassForever ? 'First-Class Forever' : 'First-Class';
  const showStandardClass = apiData?.has_standard_class;

  // Calculate total width for First-Class group header
  const fcGroupWidth = isStandard
    ? FC_GLOBAL_WIDTH_STANDARD + FC_GP_WIDTH
    : FC_GLOBAL_WIDTH + FC_ACCOUNT_WIDTH + FC_GP_WIDTH + FC_VARIANCE_WIDTH;

  // Calculate total width for Standard Class group header
  const stdGroupWidth = isStandard
    ? STD_GLOBAL_WIDTH_STANDARD + STD_GP_WIDTH
    : STD_GLOBAL_WIDTH + STD_ACCOUNT_WIDTH + STD_GP_WIDTH + STD_VARIANCE_WIDTH;

  const columns = [
    {
      field: 'tier',
      headerName: 'Tier',
      width: TIER_WIDTH,
      flex: 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.tier}</Typography>
        </Box>
      ),
    },
    {
      field: 'range',
      headerName: 'Quantity',
      width: QTY_WIDTH,
      flex: 0,
      sortable: false,
      cellClassName: 'quantity-column',
      headerClassName: 'quantity-column-header',
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.range}</Typography>
        </Box>
      ),
    },
    {
      field: 'fcGlobal',
      headerName: 'Global',
      width: isStandard ? FC_GLOBAL_WIDTH_STANDARD : FC_GLOBAL_WIDTH,
      flex: isWideScreen ? 1 : 0,
      headerAlign: 'left',
      headerClassName: 'groupSpanFirst',
      renderHeader: () => (
        <>
          <Typography className="groupHeaderSubtitle">Global</Typography>
        </>
      ),
      editable: !!isEditing && isStandard, // Only editable when is_standard is true
      sortable: false,
      renderCell: (params) => {
        if (!isEditing || !isStandard) {
          return (
            <Box className="addressWrapper">
              <Typography>{params.row.fcGlobal}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value =
          draftByRowId?.[rowId]?.fcGlobal ?? getNumericFromCurrency(params.row.fcGlobal);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), fcGlobal: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                // Update rows immediately with all calculated values using functional update
                setTierRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% based on the formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
                  let newGpPercent = '0%';
                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost; // The tier cost is the product price

                    // Get postage cost from postage_price object based on class type
                    let postageCost = 0;
                    if (useFirstClassForever) {
                      postageCost = parseFloat(apiData.postage_price?.first_class_forever) || 0;
                    } else {
                      postageCost = parseFloat(apiData.postage_price?.first_class) || 0;
                    }

                    // Get cost of goods from cost_of_good_price
                    const costOfGoods = parseFloat(apiData.cost_of_good_price) || 0;

                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  // Calculate Variance: account price - global price (if account exists)
                  let fcVariance = currentRow.fcVariance || '';
                  if (currentRow.fcAccount) {
                    const accountPrice =
                      parseFloat(getNumericFromCurrency(currentRow.fcAccount)) || 0;
                    const globalPrice = newCost;
                    const varianceValue = accountPrice - globalPrice;
                    fcVariance = varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';
                  }

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        fcGlobal: commit,
                        fcGp: newGpPercent,
                        fcVariance: fcVariance,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Maintain focus after double-click text selection
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  // Use requestAnimationFrame to ensure focus happens after selection
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                    }
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="number"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d0d0d0 !important',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ed5c2f !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                  // Ensure border stays visible even when text is selected
                  '&:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'fcAccount',
      headerName: 'Account',
      width: FC_ACCOUNT_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: !!isEditing && !isStandard, // Only editable when is_standard is false
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">Account</Typography>,
      renderCell: (params) => {
        if (!isEditing || isStandard) {
          return (
            <Box className="addressWrapper">
              <Typography>{params.row.fcAccount || '-'}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value =
          draftByRowId?.[rowId]?.fcAccount ?? getNumericFromCurrency(params.row.fcAccount);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), fcAccount: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                // Update rows immediately with all calculated values using functional update
                // This ensures we get the current row state, not stale closure values
                setTierRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% based on the formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
                  let newGpPercent = '0%';
                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost; // The account cost is the product price

                    // Get postage cost from postage_price object based on class type
                    let postageCost = 0;
                    if (useFirstClassForever) {
                      postageCost = parseFloat(apiData.postage_price?.first_class_forever) || 0;
                    } else {
                      postageCost = parseFloat(apiData.postage_price?.first_class) || 0;
                    }

                    // Get cost of goods from cost_of_good_price
                    const costOfGoods = parseFloat(apiData.cost_of_good_price) || 0;

                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  // Calculate Variance: account price - global price
                  // Use the current row's global price from the current state
                  const globalPrice = parseFloat(getNumericFromCurrency(currentRow.fcGlobal)) || 0;
                  const accountPrice = newCost;
                  const varianceValue = accountPrice - globalPrice;
                  const fcVariance =
                    varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        fcAccount: commit,
                        fcGp: newGpPercent,
                        fcVariance: fcVariance,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Maintain focus after double-click text selection
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  // Use requestAnimationFrame to ensure focus happens after selection
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                    }
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="number"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d0d0d0 !important',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ed5c2f !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                  // Ensure border stays visible even when text is selected
                  '&:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'fcGp',
      headerName: 'GP %',
      width: FC_GP_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: false,
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">GP %</Typography>,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.fcGp}</Typography>
        </Box>
      ),
    },
    {
      field: 'fcVariance',
      headerName: 'Variance',
      width: FC_VARIANCE_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: false,
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">Variance</Typography>,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.fcVariance || '-'}</Typography>
        </Box>
      ),
    },
  ];

  columns.push(
    {
      field: 'stdGlobal',
      headerName: 'Global',
      width: isStandard ? STD_GLOBAL_WIDTH_STANDARD : STD_GLOBAL_WIDTH,
      flex: isWideScreen ? 1 : 0,
      headerAlign: 'left',
      headerClassName: 'groupSpanStdFirst standard-class-first-column',
      cellClassName: 'standard-class-first-column',
      renderHeader: () => (
        <>
          <Typography className="groupHeaderSubtitle">Global</Typography>
        </>
      ),
      editable: !!isEditing && showStandardClass && isStandard, // Only editable when is_standard is true
      sortable: false,
      renderCell: (params) => {
        if (!isEditing || !showStandardClass || !isStandard) {
          return (
            <Box className="addressWrapper">
              <Typography>{showStandardClass ? params.row.stdGlobal : '-'}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value =
          draftByRowId?.[rowId]?.stdGlobal ?? getNumericFromCurrency(params.row.stdGlobal);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              size="small"
              value={value}
              variant="outlined"
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), stdGlobal: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                // Update rows immediately with all calculated values using functional update
                setTierRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% based on the formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
                  let newGpPercent = '0%';
                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost; // The tier cost is the product price

                    // Get postage cost from postage_price object for standard class
                    const postageCost = parseFloat(apiData.postage_price?.standard_class) || 0;

                    // Get cost of goods from cost_of_good_price
                    const costOfGoods = parseFloat(apiData.cost_of_good_price) || 0;

                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        stdGlobal: commit,
                        stdGp: newGpPercent,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Maintain focus after double-click text selection
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  // Use requestAnimationFrame to ensure focus happens after selection
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                    }
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="number"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d0d0d0 !important',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ed5c2f !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                  // Ensure border stays visible even when text is selected
                  '&:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'stdAccount',
      headerName: 'Account',
      width: STD_ACCOUNT_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: !!isEditing && !isStandard && showStandardClass, // Only editable when is_standard is false
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">Account</Typography>,
      renderCell: (params) => {
        if (!isEditing || isStandard || !showStandardClass) {
          return (
            <Box className="addressWrapper">
              <Typography>{showStandardClass ? params.row.stdAccount || '-' : '-'}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value =
          draftByRowId?.[rowId]?.stdAccount ?? getNumericFromCurrency(params.row.stdAccount);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), stdAccount: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                setTierRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% for standard class
                  let newGpPercent = '0%';
                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost;
                    const postageCost = parseFloat(apiData.postage_price?.standard_class) || 0;
                    const costOfGoods = parseFloat(apiData.cost_of_good_price) || 0;
                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  // Calculate Variance: account price - global price (standard class)
                  const globalPrice = parseFloat(getNumericFromCurrency(currentRow.stdGlobal)) || 0;
                  const accountPrice = newCost;
                  const varianceValue = accountPrice - globalPrice;
                  const stdVariance =
                    varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        stdAccount: commit,
                        stdGp: newGpPercent,
                        stdVariance: stdVariance,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => {
                e.stopPropagation();
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) input.focus();
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="text"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d0d0d0 !important' },
                  '&:hover fieldset': { borderColor: '#ed5c2f !important' },
                  '&.Mui-focused fieldset, &:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'stdGp',
      headerName: 'GP %',
      width: STD_GP_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: false,
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">GP %</Typography>,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{showStandardClass ? params.row.stdGp : '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'stdVariance',
      headerName: 'Variance',
      width: STD_VARIANCE_WIDTH,
      flex: 0,
      headerAlign: 'left',
      editable: false,
      sortable: false,
      renderHeader: () => <Typography className="groupHeaderSubtitle">Variance</Typography>,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{showStandardClass ? params.row.stdVariance || '-' : '-'}</Typography>
        </Box>
      ),
    }
  );

  // Filter columns based on is_standard
  // Tier and range columns are always shown
  const filteredColumns = columns.filter((col) => {
    // Always show tier and range columns
    if (col.field === 'tier' || col.field === 'range') {
      return true;
    }

    // If is_standard is true, only show Global and GP% for both First-Class and Standard Class
    if (isStandard) {
      return (
        col.field === 'fcGlobal' ||
        col.field === 'fcGp' ||
        col.field === 'stdGlobal' ||
        col.field === 'stdGp'
      );
    }
    // If is_standard is false, show all columns
    return true;
  });

  return filteredColumns;
};

const buildAddonColumns = ({
  isWideScreen,
  isEditing,
  draftByRowId,
  setDraftByRowId,
  setAddonRows,
  apiData,
  calculateGPPercent,
  isStandard,
}) => {
  const columns = [
    {
      field: 'addon',
      headerName: 'Add-On',
      width: 240,
      flex: isWideScreen ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="fullText">
          <TruncatedText text={params.row.addon || ''} />
        </Box>
      ),
    },
    {
      field: 'global',
      headerName: 'Global',
      width: 140,
      flex: isWideScreen ? 1 : 0,
      sortable: false,
      editable: !!isEditing && isStandard, // Only editable when is_standard is true
      renderCell: (params) => {
        if (!isEditing || !isStandard) {
          return (
            <Box className="addressWrapper">
              <Typography>{params.row.global}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value = draftByRowId?.[rowId]?.global ?? getNumericFromCurrency(params.row.global);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), global: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                // Update rows immediately with all calculated values using functional update
                setAddonRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% based on the formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
                  // For Add On section, use postage price as 0
                  let newGpPercent = '0%';

                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost; // The addon global cost is the product price

                    // For addons, postage cost is always 0
                    const postageCost = 0;

                    // Get cost of goods from cost_of_good_price
                    const costOfGoods = parseFloat(currentRow.costOfGoods) || 0;

                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        global: commit,
                        gp: newGpPercent,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Maintain focus after double-click text selection
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  // Use requestAnimationFrame to ensure focus happens after selection
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                    }
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="number"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d0d0d0 !important',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ed5c2f !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                  // Ensure border stays visible even when text is selected
                  '&:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'account',
      headerName: 'Account',
      width: 140,
      flex: isWideScreen ? 1 : 0,
      sortable: false,
      editable: !!isEditing && !isStandard, // Only editable when is_standard is false
      renderCell: (params) => {
        if (!isEditing || isStandard) {
          return (
            <Box className="addressWrapper">
              <Typography>{params.row.account || '-'}</Typography>
            </Box>
          );
        }
        const rowId = params.row.id;
        const value = draftByRowId?.[rowId]?.account ?? getNumericFromCurrency(params.row.account);
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => {
                const next = getNumericFromCurrency(e.target.value);

                // Update draft state
                setDraftByRowId((prev) => ({
                  ...prev,
                  [rowId]: { ...(prev[rowId] || {}), account: next },
                }));

                // Commit formatted value immediately
                const commit = toCurrencyString(next);

                // Calculate GP% and Variance in real-time using the new value
                const newCost = parseFloat(next) || 0;

                // Update rows immediately with all calculated values using functional update
                // This ensures we get the current row state, not stale closure values
                setAddonRows((prev) => {
                  const currentRow = prev.find((r) => r.id === rowId);
                  if (!currentRow) return prev;

                  // Calculate GP% based on the formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
                  // For Add On section, use postage price as 0
                  let newGpPercent = '0%';

                  if (apiData && calculateGPPercent) {
                    const productPrice = newCost; // The addon account cost is the product price

                    // For addons, postage cost is always 0
                    const postageCost = 0;

                    // Get cost of goods from cost_of_good_price
                    const costOfGoods = parseFloat(apiData.cost_of_good_price) || 0;

                    const gpPercent = calculateGPPercent(productPrice, postageCost, costOfGoods);
                    newGpPercent = `${gpPercent}%`;
                  }

                  // Calculate Variance: account price - global price
                  // Use the current row's global price from the current state
                  const globalPrice = parseFloat(getNumericFromCurrency(currentRow.global)) || 0;
                  const accountPrice = newCost;
                  const varianceValue = accountPrice - globalPrice;
                  const variance =
                    varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';

                  return prev.map((r) => {
                    if (r.id === rowId) {
                      return {
                        ...r,
                        account: commit,
                        gp: newGpPercent,
                        variance: variance,
                      };
                    }
                    return r;
                  });
                });
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Maintain focus after double-click text selection
                const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
                if (input) {
                  // Use requestAnimationFrame to ensure focus happens after selection
                  requestAnimationFrame(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                    }
                  });
                }
              }}
              inputProps={{ inputMode: 'decimal', step: '0.001' }}
              type="number"
              fullWidth
              className="cellInput"
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d0d0d0 !important',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ed5c2f !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                  // Ensure border stays visible even when text is selected
                  '&:focus-within fieldset': {
                    borderColor: '#ed5c2f !important',
                    borderWidth: '1px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      },
    },
    {
      field: 'gp',
      headerName: 'GP %',
      width: 120,
      flex: isWideScreen ? 1 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="addressWrapper">
            <Typography>{params.row.gp}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'variance',
      headerName: 'Variance',
      width: 150,
      flex: isWideScreen ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.variance || '-'}</Typography>
        </Box>
      ),
    },
  ];

  // Filter columns based on is_standard
  // Tier and range columns are always shown
  const filteredColumns = columns.filter((col) => {
    // Always show tier and range columns
    if (col.field === 'addon') {
      return true;
    }

    // If is_standard is false, only show Global and GP% for both First-Class and Standard Class
    if (isStandard) {
      return col.field === 'global' || col.field === 'gp';
    }
    // If is_standard is false, show all columns
    return true;
  });

  return filteredColumns;
};

const buildSummaryColumns = () => [
  { field: 'effectiveDate', headerName: 'Effective Date', flex: 1, width: 200, sortable: false },
  { field: 'status', headerName: 'Status', flex: 1, width: 200, sortable: false },
  { field: 'changeType', headerName: 'Change Type', flex: 1, width: 200, sortable: false },
  { field: 'notes', headerName: 'Notes', flex: 1, width: 200, sortable: false },
];

// dummy rows now sourced from ~/utils/constants

const Pricing = () => {
  const dispatch = useDispatch();
  const isWideScreen = useScreenWidth(1380);
  const isWideScreenTier = useScreenWidth(1650);

  const [account, setAccount] = useState(null);
  const [product, setProduct] = useState(null);
  const [version, setVersion] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  // Filters data from API
  const [accountOptions, setAccountOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [versionOptions, setVersionOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false); // Track if we've attempted to fetch data
  const [rawVersionsData, setRawVersionsData] = useState([]); // Store raw version data to check is_standard
  const isInitialMount = useRef(true); // Track if this is the initial component mount

  // API response data for table structure
  const [apiResponseData, setApiResponseData] = useState(null);
  const [addonRows, setAddonRows] = useState([]);
  const [versionDetailsRows, setVersionDetailsRows] = useState([]);

  // Redux state
  const tiersData = useSelector((state) => state.adminPricingReducers.tiers);
  const { data, loading } = tiersData;
  const [isEditing, setIsEditing] = useState(false);
  const [tierRows, setTierRows] = useState([]);
  const [updatePricingOpen, setUpdatePricingOpen] = useState(false);
  const [draftByRowId, setDraftByRowId] = useState({});
  const [addonDraftByRowId, setAddonDraftByRowId] = useState({});
  const [validationErrors, setValidationErrors] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    header: 'Delete Pricing Version',
    message:
      'Are you sure you want to delete this pricing version? This will remove all items associated with the same Effective Date.',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });
  const [confirmOverrideModal, setConfirmOverrideModal] = useState({
    open: false,
    header: 'Override Pricing Version',
    message:
      'A Pricing Version already exists for the selected Effective Date. Do you want to overwrite the existing Pricing version with this new one?',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });

  // Calculate GP% based on formula: GP% = (Product Price − (Postage Cost + Cost of Goods)) / Product Price
  const calculateGPPercent = (productPrice, postageCost, costOfGoods) => {
    if (!productPrice || productPrice <= 0) return 0;

    const totalCosts = (postageCost || 0) + (costOfGoods || 0);
    const gpPercent = ((productPrice - totalCosts) / productPrice) * 100;

    return (0, Math.round(gpPercent * 100) / 100); // Round to 2 decimal places, minimum 0
  };

  const useFirstClassForever = apiResponseData?.has_first_class_forever;
  const firstClassLabel = useFirstClassForever ? 'First-Class Forever' : 'First-Class';

  const tierColumnGroupingModel = useMemo(
    () => [
      {
        groupId: 'firstClass',
        headerName: firstClassLabel,
        children: [
          { field: 'fcGlobal' },
          { field: 'fcAccount' },
          { field: 'fcGp' },
          { field: 'fcVariance' },
        ],
      },
      {
        groupId: 'standardClass',
        headerName: 'Standard Class',
        children: [
          { field: 'stdGlobal' },
          { field: 'stdAccount' },
          { field: 'stdGp' },
          { field: 'stdVariance' },
        ],
      },
    ],
    [firstClassLabel]
  );

  const tierColumns = useMemo(
    () =>
      buildTierColumns({
        isEditing,
        draftByRowId,
        setDraftByRowId,
        setTierRows,
        apiData: apiResponseData,
        calculateGPPercent,
        isStandard: account?.is_standard === true,
        isWideScreen: isWideScreenTier,
        tierRows, // Add tierRows to ensure columns re-render when rows update
      }),
    [isEditing, draftByRowId, apiResponseData, account, isWideScreenTier, tierRows]
  );

  const addonColumns = useMemo(
    () =>
      buildAddonColumns({
        isWideScreen,
        isEditing,
        draftByRowId: addonDraftByRowId,
        setDraftByRowId: setAddonDraftByRowId,
        setAddonRows,
        apiData: apiResponseData,
        calculateGPPercent,
        isStandard: account?.is_standard === true,
        addonRows, // Add addonRows to ensure columns re-render when rows update
      }),
    [isWideScreen, isEditing, addonDraftByRowId, apiResponseData, account, addonRows]
  );

  const summaryColumns = useMemo(() => buildSummaryColumns(), []);

  const handleProcessRowUpdate = (newRow, oldRow) => {
    if (newRow?.disabled) return oldRow;

    // Calculate Variance when account values change
    let updatedRow = { ...newRow };

    // Calculate fcVariance if fcAccount changed
    if (newRow.fcAccount !== oldRow.fcAccount) {
      const globalPrice = parseFloat(getNumericFromCurrency(newRow.fcGlobal)) || 0;
      const accountPrice = parseFloat(getNumericFromCurrency(newRow.fcAccount)) || 0;
      const varianceValue = accountPrice - globalPrice;
      updatedRow.fcVariance = varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';
    }

    // Calculate stdVariance if stdAccount changed
    if (newRow.stdAccount !== oldRow.stdAccount) {
      const globalPrice = parseFloat(getNumericFromCurrency(newRow.stdGlobal)) || 0;
      const accountPrice = parseFloat(getNumericFromCurrency(newRow.stdAccount)) || 0;
      const varianceValue = accountPrice - globalPrice;
      updatedRow.stdVariance = varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';
    }

    setTierRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
    return updatedRow;
  };

  const handleProcessAddonRowUpdate = (newRow, oldRow) => {
    if (newRow?.disabled) return oldRow;

    // Calculate Variance when account value changes
    let updatedRow = { ...newRow };

    if (newRow.account !== oldRow.account) {
      const globalPrice = parseFloat(getNumericFromCurrency(newRow.global)) || 0;
      const accountPrice = parseFloat(getNumericFromCurrency(newRow.account)) || 0;
      const varianceValue = accountPrice - globalPrice;
      updatedRow.variance = varianceValue !== 0 ? toCurrencyString(String(varianceValue)) : '';
    }

    setAddonRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
    return updatedRow;
  };

  const isCellEditable = (params) => {
    if (!isEditing) return false;
    if (params?.row?.disabled) return false;
    const isStandard = account?.is_standard === true;

    // When is_standard is true: Global columns are editable
    // When is_standard is false: Account columns are editable
    if (isStandard) {
      return params.field === 'fcGlobal' || params.field === 'stdGlobal';
    } else {
      return params.field === 'fcAccount' || params.field === 'stdAccount';
    }
  };

  const isAddonCellEditable = (params) => {
    if (!isEditing) return false;
    if (params?.row?.disabled) return false;
    const isStandard = account?.is_standard === true;

    // When is_standard is true: Global column is editable
    // When is_standard is false: Account column is editable
    if (isStandard) {
      return params.field === 'global';
    } else {
      return params.field === 'account';
    }
  };

  const fetchFiltersData = async () => {
    try {
      setFiltersLoading(true);
      const response = await dispatch(fetchAdminPricingTiersFiltersData());
      if (response.status === 200) {
        const { data } = response.data;

        // Map account options
        let mappedAccounts = [];
        if (data.accounts && Array.isArray(data.accounts)) {
          mappedAccounts = data.accounts.map((account) => ({
            id: account.id || account.value,
            title: account.name || account.label || account.title,
            is_default: account.is_default || false,
            is_standard: account.is_standard || false,
          }));
          setAccountOptions(mappedAccounts);
        }

        // Map product options
        let mappedProducts = [];
        if (data.products && Array.isArray(data.products)) {
          mappedProducts = data.products.map((product) => ({
            id: product.id || product.value,
            title: product.name || product.label || product.title,
          }));
          setProductOptions(mappedProducts);
        }

        const allStandardAccount = mappedAccounts.find((acc) => acc.is_default == true);

        const fourBySixPostcard = mappedProducts.find(
          (prod) => prod.title === '4x6 Postcard' || prod.title?.includes('4x6 Postcard')
        );

        if (allStandardAccount) {
          setAccount(allStandardAccount);
        }
        if (fourBySixPostcard) {
          setProduct(fourBySixPostcard);
        }
      }
    } catch (error) {
      console.error('Failed to fetch filters data:', error);
      dispatch(failure('Failed to load filters data'));
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchVersionsData = async (
    selectedProduct,
    selectedAccount,
    selectVersionId = null,
    autoSelectDefault = false
  ) => {
    try {
      setVersionsLoading(true);
      setVersionOptions([]); // Clear previous versions
      setRawVersionsData([]); // Clear raw versions data

      const payload = {};

      // Handle single value (not array)
      const productValue = selectedProduct;
      if (productValue) {
        payload.product = productValue.id;
      }

      payload.account_id = selectedAccount.id || 0;

      const response = await dispatch(fetchAdminPricingTiersVersionsData(payload));
      if (response.status === 200) {
        const { data } = response.data;
        // Map version options and store raw data
        if (data && Array.isArray(data)) {
          setRawVersionsData(data); // Store raw version data
          const mappedVersions = data.map((version) => ({
            id: version.id,
            title: (version?.version || '').replace(/,/g, ''),
            is_default: version?.is_default || false,
          }));
          setVersionOptions(mappedVersions);

          // If selectVersionId is provided, select that version
          if (selectVersionId) {
            const targetVersion = mappedVersions.find((version) => version.id === selectVersionId);
            if (targetVersion) {
              // Set loading before selecting version to show loader immediately
              setTableLoading(true);
              setTimeout(() => {
                setVersion(targetVersion);
              }, 0);
            } else {
              // If version not found, stop loading
              setTableLoading(false);
            }
          } else if (autoSelectDefault) {
            // Find all default versions and select the latest one (highest ID)
            const defaultVersions = mappedVersions.filter((version) => version.is_default === true);
            let versionToSelect = null;

            if (defaultVersions.length > 0) {
              // Sort by ID descending to get the latest default version
              defaultVersions.sort((a, b) => b.id - a.id);
              versionToSelect = defaultVersions[0];
            } else if (mappedVersions.length > 0) {
              // If no default version, select the first one
              versionToSelect = mappedVersions[0];
            }

            if (versionToSelect) {
              // Set loading before selecting version to show loader immediately
              setTableLoading(true);
              setTimeout(() => {
                setVersion(versionToSelect);
              }, 0);
            } else {
              // If no version found, stop loading
              setTableLoading(false);
            }
          } else {
            // If no version is being auto-selected, stop loading
            setTableLoading(false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch versions data:', error);
      dispatch(failure('Failed to load versions data'));
      setTableLoading(false);
    } finally {
      setVersionsLoading(false);
    }
  };

  const fetchTieredPricingData = async (selectedVersion) => {
    try {
      if (selectedVersion) {
        setTableLoading(true);

        const versionId = selectedVersion.id;
        setSelectedVersionId(versionId);

        const response = await dispatch(fetchAdminTieredPricingData(versionId));

        if (response.status === 200) {
          const responseData = response.data.data || response.data;
          setApiResponseData(responseData);

          // Map tiers data to table format
          if (responseData.tiers && Array.isArray(responseData.tiers)) {
            const useFirstClassForever = responseData.has_first_class_forever;
            const isStandard = account?.is_standard === true;

            const mappedTiers = responseData.tiers.map((tier, index) => {
              const firstClassData = useFirstClassForever
                ? tier.first_class_forever
                : tier.first_class;

              // Format helper function
              const formatCurrency = (value) => {
                if (value == null || value === undefined) return '';
                return `$${parseFloat(value).toFixed(3)}`;
              };

              const formatPercent = (value) => {
                if (value == null || value === undefined) return '0%';
                return `${value}%`;
              };

              // When is_standard is false: Global from global_cost, Account from cost, GP% from gp_percent, Variance from variance_cost
              // When is_standard is true: Global from cost, GP% from gp_percent
              const fcGlobal = isStandard
                ? formatCurrency(firstClassData.cost)
                : formatCurrency(firstClassData.global_cost);
              const fcAccount = isStandard ? '' : formatCurrency(firstClassData.cost);
              const fcGp = isStandard
                ? formatPercent(firstClassData.gp_percent)
                : formatPercent(firstClassData.gp_percent || firstClassData.global_gp_percent);
              const fcVariance = isStandard ? '' : formatCurrency(firstClassData.variance_cost);

              const stdGlobal = responseData.has_standard_class
                ? isStandard
                  ? formatCurrency(tier.standard_class.cost)
                  : formatCurrency(tier.standard_class.global_cost)
                : '';
              const stdAccount =
                responseData.has_standard_class && !isStandard
                  ? formatCurrency(tier.standard_class.cost)
                  : '';
              const stdGp = responseData.has_standard_class
                ? isStandard
                  ? formatPercent(tier.standard_class.gp_percent)
                  : formatPercent(
                      tier.standard_class.gp_percent || tier.standard_class.global_gp_percent
                    )
                : '';
              const stdVariance =
                responseData.has_standard_class && !isStandard
                  ? formatCurrency(tier.standard_class.variance_cost)
                  : '';

              return {
                id: tier.tier || `tier_${index + 1}`,
                tierId: tier.id || tier.tier || `tier_${index + 1}`,
                tier: tier.tier_label || tier.tier,
                range: tier.qty,
                fcGlobal,
                fcAccount,
                fcGp,
                fcVariance,
                stdGlobal,
                stdAccount,
                stdGp,
                stdVariance,
                is_disabled: tier.is_disabled || false,
              };
            });
            setTierRows(mappedTiers);
          }

          // Map addons data to addon table format
          if (responseData.addons && Array.isArray(responseData.addons)) {
            const isStandard = account?.is_standard === true;

            // Format helper function
            const formatCurrency = (value) => {
              if (value == null || value === undefined) return '';
              return `$${parseFloat(value).toFixed(3)}`;
            };

            const formatPercent = (value) => {
              if (value == null || value === undefined) return '0%';
              return `${value}%`;
            };

            const mappedAddons = responseData.addons.map((addon) => {
              // When is_standard is false: Global from global_cost, Account from cost, GP% from global_gp_percent, Variance from variance_cost
              // When is_standard is true: Global from cost, GP% from gp_percent
              const global = isStandard
                ? formatCurrency(addon.cost)
                : formatCurrency(addon.global_cost);
              const account = isStandard ? '' : formatCurrency(addon.cost);
              const gp = isStandard
                ? formatPercent(addon.gp_percent)
                : formatPercent(addon.global_gp_percent || addon.gp_percent);
              const variance = isStandard ? '' : formatCurrency(addon.variance_cost);

              return {
                id: addon.key,
                addon: addon.label,
                costOfGoods: addon.cost_of_good_price,
                global,
                account,
                gp,
                variance,
              };
            });
            setAddonRows(mappedAddons);
          }

          // Map version details data
          const versionDetails = [
            {
              id: responseData.id || 1,
              effectiveDate: responseData.effective_date || '',
              status: responseData.status_label || 'N/A',
              changeType: responseData.change_type_label || responseData.change_type || '',
              notes: responseData?.notes || '',
            },
          ];
          setVersionDetailsRows(versionDetails);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tiered pricing data:', error);
      dispatch(failure('Failed to load tiered pricing data'));
    } finally {
      setTableLoading(false);
    }
  };

  const handleDeletePricingTier = async () => {
    try {
      if (!version || !selectedVersionId) {
        dispatch(failure('Please select a version first'));
        return;
      }

      const tierId = version?.id || !selectedVersionId;

      if (!tierId) {
        dispatch(failure('Tier ID not found'));
        return;
      }

      const response = await dispatch(deleteAdminPricingTier(tierId));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Pricing tier deleted successfully'));

        // Clear all table data
        clearTableData();

        // Clear draft values
        setDraftByRowId({});
        setAddonDraftByRowId({});

        // Clear current version to prevent fetching the deleted tier
        setVersion(null);
        setSelectedVersionId(null);

        // Re-fetch versions and select the default version, then fetch its tier data
        if (account && product) {
          // Set loading state immediately when fetching versions after deletion
          setTableLoading(true);

          // Fetch versions and get the response data
          const payload = {
            product: product.id,
            account_id: account.id || 0,
          };

          const versionsResponse = await dispatch(fetchAdminPricingTiersVersionsData(payload));

          if (versionsResponse.status === 200) {
            const { data } = versionsResponse.data;

            if (data && Array.isArray(data)) {
              // Update raw versions data and version options
              setRawVersionsData(data);
              const mappedVersions = data.map((version) => ({
                id: version.id,
                title: (version?.version || '').replace(/,/g, ''),
                is_default: version?.is_default || false,
              }));
              setVersionOptions(mappedVersions);

              // Find and select the default version
              const defaultVersion = mappedVersions.find((v) => v.is_default === true);

              if (defaultVersion) {
                // Select the default version
                setVersion(defaultVersion);
                setSelectedVersionId(defaultVersion.id);

                // Fetch tier data for the default version
                await fetchTieredPricingData(defaultVersion);
              } else if (mappedVersions.length > 0) {
                // If no default version found, select the first one
                const firstVersion = mappedVersions[0];
                setVersion(firstVersion);
                setSelectedVersionId(firstVersion.id);
                await fetchTieredPricingData(firstVersion);
              }
            }
          }
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to delete pricing tier'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to delete pricing tier')
      );
    } finally {
      setConfirmDeleteModal((m) => ({ ...m, loading: false, open: false }));
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchFiltersData();
  }, []);

  // Update tierRows when data is loaded
  useEffect(() => {
    if (data.length > 0) {
      setTierRows(data);
    }
  }, [data]);

  // Clear table data when any dropdown is cleared
  const clearTableData = () => {
    setTierRows([]);
    setSelectedVersionId(null);
    setAddonRows([]);
    setVersionDetailsRows([]);
    setApiResponseData(null);
  };

  const formatTierPricingPayload = (effectiveDate, notes, forceUpdate = false) => {
    const payload = {
      effective_date: formatDateForAPI(effectiveDate),
      notes: notes,
      force_update: forceUpdate,
      account_id: account?.id || null,
      tiers: [],
      addons: [],
    };

    // Format tiers data
    if (tierRows && tierRows.length > 0) {
      payload.tiers = tierRows.map((tier) => {
        const tierData = {
          tier: tier.id || tier.tier,
        };

        const isStandard = account?.is_standard === true;

        // Add first_class data
        // When is_standard is false, use Account value for cost; when true, use Global value
        const fcCostValue = isStandard
          ? tier.fcGlobal || ''
          : tier.fcAccount || tier.fcGlobal || '';

        if (fcCostValue) {
          tierData.first_class = {
            cost: parseFloat(fcCostValue.replace('$', '')) || 0,
            gp_percent: parseFloat(tier.fcGp?.replace('%', '')) || 0,
          };
        }

        // Add standard_class data if available
        // When is_standard is false, use Account value for cost; when true, use Global value
        if (apiResponseData?.has_standard_class) {
          const stdCostValue = isStandard
            ? tier.stdGlobal || ''
            : tier.stdAccount || tier.stdGlobal || '';

          if (stdCostValue) {
            tierData.standard_class = {
              cost: parseFloat(stdCostValue.replace('$', '')) || 0,
              gp_percent: parseFloat(tier.stdGp?.replace('%', '')) || 0,
            };
          }
        }

        // Add first_class_forever data if available
        if (apiResponseData?.has_first_class_forever) {
          const fcForeverCostValue = isStandard
            ? tier.fcGlobal || ''
            : tier.fcAccount || tier.fcGlobal || '';

          if (fcForeverCostValue) {
            tierData.first_class_forever = {
              cost: parseFloat(fcForeverCostValue.replace('$', '')) || 0,
              gp_percent: parseFloat(tier.fcGp?.replace('%', '')) || 0,
            };
          }
        }

        return tierData;
      });
    }

    // Format addons data
    if (addonRows && addonRows.length > 0) {
      const isStandard = account?.is_standard === true;
      payload.addons = addonRows.map((addon) => {
        // When is_standard is false, use Account value for cost; when true, use Global value
        const costValue = isStandard ? addon.global || '' : addon.account || addon.global || '';

        return {
          key: addon.id,
          cost: parseFloat(costValue.replace('$', '')) || 0,
          gp_percent: parseFloat(addon.gp?.replace('%', '')) || 0,
        };
      });
    }

    return payload;
  };

  const handleUpdatePricingTiers = async (
    effectiveDate,
    notes,
    forceUpdate = false,
    overrideId = null
  ) => {
    try {
      if (!tierRows.length && !addonRows.length) {
        dispatch(failure('No data to update'));
        return;
      }

      const payload = formatTierPricingPayload(effectiveDate, notes, forceUpdate);
      // Use overrideId if provided (for forced updates), otherwise use selectedVersionId
      const versionIdToUse = forceUpdate && overrideId ? overrideId : selectedVersionId;

      const response = await dispatch(updateAdminPricingTiers(payload, versionIdToUse));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Pricing tiers updated successfully'));
        // Clear all draft values
        setDraftByRowId({});
        setAddonDraftByRowId({});
        // Clear validation errors
        setValidationErrors(null);

        // Close override modal if it was open
        if (forceUpdate) {
          setUpdatePricingOpen(false);
          closeOverRideModal();
        }

        // Extract the latest tier ID from response
        const latestTierId = response.data?.data?.id || response.data?.id;

        // Re-fetch versions to update the dropdown and fetch latest tier data in parallel
        if (account && product && latestTierId) {
          // Fetch versions with the latest tier ID to auto-select it, and fetch latest tier data in parallel
          await Promise.all([
            fetchVersionsData(product, account, latestTierId),
            // Fetch latest tier data using the new tier ID
            (async () => {
              // Create a version object with the latest tier ID
              const latestVersion = { id: latestTierId };
              await fetchTieredPricingData(latestVersion);
            })(),
          ]);
          setIsEditing(false);
          setUpdatePricingOpen(false);
        } else if (version) {
          // Fallback: if no latest tier ID, just refresh with current version
          await fetchTieredPricingData(version);
        }
        return response;
      } else if (response.status === 422) {
        // Check if override modal should be shown
        if (response.data.data && response.data.data.show_override_modal && !forceUpdate) {
          dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            loading: false,
            handleOk: () => {
              setConfirmOverrideModal((m) => ({ ...m, loading: true }));
              // Use update function with forceUpdate=true and overrideId
              handleUpdatePricingTiers(effectiveDate, notes, true, id).finally(() => {
                setConfirmOverrideModal((m) => ({ ...m, loading: false }));
              });
            },
            handleClose: closeOverRideModal,
          }));
          return;
        } else {
          // Set validation errors to show in modal instead of snackbar
          const errors = response.data?.data?.errors || response.data?.errors || null;
          setValidationErrors(errors);
          // Keep modal open to show errors
          // Don't show snackbar for validation errors
          return response;
        }
      } else {
        await fetchTieredPricingData(version);
        dispatch(failure(response?.data?.message || 'Failed to update pricing tiers'));
        return response;
      }
    } catch (error) {
      await fetchTieredPricingData(version);
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to update pricing tiers'
        )
      );
      throw error;
    } finally {
    }
  };

  const closeOverRideModal = () => {
    setConfirmOverrideModal((prev) => ({
      ...prev,
      open: false,
      loading: false,
      handleOk: () => {},
      handleClose: undefined,
    }));
  };

  // Custom handlers to clear other filters when one is changed
  const handleAccountChange = (newAccount) => {
    // MultiSelect returns an array, extract first value or null
    const accountValue = Array.isArray(newAccount)
      ? newAccount.length > 0
        ? newAccount[0]
        : null
      : newAccount;
    setAccount(accountValue);
    setProduct(null);
    setVersion(null);
    clearTableData();
  };

  const handleProductChange = (newProduct) => {
    // MultiSelect returns an array, extract first value or null
    const productValue = Array.isArray(newProduct)
      ? newProduct.length > 0
        ? newProduct[0]
        : null
      : newProduct;
    setProduct(productValue);
    setVersion(null);
    setIsEditing(false);
    setDraftByRowId({});
    setAddonDraftByRowId({});
    clearTableData();
  };

  const handleVersionChange = (newVersion) => {
    // MultiSelect returns an array, extract first value or null
    const versionValue = Array.isArray(newVersion)
      ? newVersion.length > 0
        ? newVersion[0]
        : null
      : newVersion;
    setVersion(versionValue);
    setIsEditing(false);
    setDraftByRowId({});
    setAddonDraftByRowId({});
    clearTableData();
  };

  // Fetch versions when account or product selection changes
  useEffect(() => {
    if (account && product) {
      // Set loading state immediately when account and product are selected
      // This ensures loader shows on initial mount instead of "No Results Found"
      setTableLoading(true);
      setHasAttemptedFetch(true); // Mark that we've attempted to fetch

      // Always auto-select default version when account or product changes
      const shouldAutoSelect = true;
      if (isInitialMount.current) {
        isInitialMount.current = false; // Mark that initial mount is done
      }
      fetchVersionsData(product, account, null, shouldAutoSelect);
    } else {
      // Clear versions if no account or product selected
      setVersionOptions([]);
      setVersion(null);
      clearTableData();
      setTableLoading(false);
      setHasAttemptedFetch(false); // Reset fetch attempt flag
    }
  }, [account, product]);

  // Fetch tiered pricing when all three options are selected
  useEffect(() => {
    if (account && product && version) {
      fetchTieredPricingData(version);
    } else {
      // Clear table data if any selection is missing
      clearTableData();
    }
  }, [account, product, version, versionOptions]);

  return (
    <Container maxWidth="xxl" className="olcAdminSectionPage">
      <Box className="pricingWrapper">
        <Box className="topFilters">
          <Box className="pricingControlsGroup">
            <Box className="pricingTableHeader">
              <Typography variant="h6" className="pricingControlsSectionTitle">
                Pricing Tiers
              </Typography>
            </Box>

            <Box className="filterGroup">
              <OlcDropdownSearch
                options={accountOptions}
                selectedValue={account}
                setSelectedValue={handleAccountChange}
                clearable={true}
                placeHolderText="Account"
                noOptionText="No options found"
                component="multipleIds"
                searchAutocomplete
                searchFieldPlaceholderText="for account"
                className={account ? 'activeState filterControl' : 'inactiveState filterControl'}
                activeState={true}
                showSelectedValueTooltip
              />

              <OlcDropdownSearch
                options={productOptions}
                selectedValue={product}
                setSelectedValue={handleProductChange}
                clearable={true}
                placeHolderText="Product"
                noOptionText="No options found"
                component="multipleIds"
                searchAutocomplete
                searchFieldPlaceholderText="product"
                className={product ? 'activeState filterControl' : 'inactiveState filterControl'}
                activeState={true}
                showSelectedValueTooltip
              />

              <OlcDropdownSearch
                options={versionOptions}
                selectedValue={version}
                setSelectedValue={handleVersionChange}
                clearable={true}
                placeHolderText="Pricing Version"
                noOptionText="No options found"
                component="multipleIds"
                searchAutocomplete
                searchFieldPlaceholderText="version"
                className={version ? 'activeState filterControl' : 'inactiveState filterControl'}
                activeState={true}
                showSelectedValueTooltip
              />

              {version &&
                versionDetailsRows.length > 0 &&
                versionDetailsRows[0]?.status?.toLowerCase() !== 'active' && (
                  <OlcButton
                    variant="secondary"
                    className="deleteBtn"
                    onClick={() =>
                      setConfirmDeleteModal((m) => ({
                        ...m,
                        open: true,
                        handleOk: () => {
                          version ? handleDeletePricingTier() : undefined;
                        },
                        handleClose: () => {
                          setConfirmDeleteModal((mm) => ({ ...mm, open: false, loading: false }));
                        },
                      }))
                    }
                  >
                    Delete
                  </OlcButton>
                )}
            </Box>
          </Box>

          <Box className="pricingActions">
            {version && (
              <OlcButton
                variant="primary"
                className="updateBtn"
                onClick={() => {
                  if (!isEditing) {
                    setIsEditing(true);
                  } else {
                    setValidationErrors(null); // Clear previous errors when opening modal
                    setUpdatePricingOpen(true);
                  }
                }}
              >
                {isEditing ? 'Save Pricing' : 'Update Pricing'}
              </OlcButton>
            )}
            {isEditing && version && (
              <OlcButton
                variant="secondary"
                className="deleteBtn"
                onClick={async () => {
                  setDraftByRowId({});
                  setAddonDraftByRowId({});
                  setIsEditing(false);
                  if (version) {
                    await fetchTieredPricingData(version);
                  }
                }}
              >
                Cancel
              </OlcButton>
            )}
          </Box>
        </Box>

        <Box className="pricingSection">
          <PricingControlsTable
            className="pricing-controls-table pricing-controls-table--grouped"
            rows={tierRows}
            columns={tierColumns}
            columnGroupingModel={tierColumnGroupingModel}
            columnHeaderHeight={48}
            columnGroupHeaderHeight={32}
            pagination={{}}
            processRowUpdate={handleProcessRowUpdate}
            isCellEditable={isCellEditable}
            showPagination={false}
            loading={tableLoading}
            showNoRows={hasAttemptedFetch && !tableLoading && tierRows.length === 0}
            disabledFlagKey="is_disabled"
            disabledRowClass="disabled-row"
          />
        </Box>

        {addonRows.length > 0 && (
          <Box className="pricingSection">
            <Box className="sectionHeader addonsHeader">
              <Typography variant="h6" className="pricingControlsSectionTitle">
                Add-Ons
              </Typography>
            </Box>
            <PricingControlsTable
              className="pricing-controls-table"
              rows={addonRows}
              columns={addonColumns}
              pagination={{}}
              processRowUpdate={handleProcessAddonRowUpdate}
              isCellEditable={isAddonCellEditable}
              showPagination={false}
              loading={tableLoading}
              showNoRows={hasAttemptedFetch && !tableLoading && addonRows.length === 0}
            />
          </Box>
        )}

        <Box className="pricingSection">
          <Box className="sectionHeader summaryHeader">
            <Typography variant="h6" className="pricingControlsSectionTitle">
              Version Details
            </Typography>
          </Box>
          <PricingControlsTable
            className="pricing-controls-table"
            rows={versionDetailsRows}
            columns={summaryColumns}
            pagination={{}}
            showPagination={false}
            loading={tableLoading}
            showNoRows={hasAttemptedFetch && !tableLoading && versionDetailsRows.length === 0}
          />
        </Box>
        <ConfirmModal openConfirmModal={confirmDeleteModal} />
        <ConfirmModal openConfirmModal={confirmOverrideModal} />
        <UpdatePricingModal
          open={updatePricingOpen}
          onClose={() => {
            setUpdatePricingOpen(false);
            setValidationErrors(null); // Clear errors when modal closes
          }}
          onSubmit={async (formData) => {
            try {
              setValidationErrors(null); // Clear previous errors
              const result = await handleUpdatePricingTiers(
                formData.effectiveDate,
                formData.notes,
                false // forceUpdate defaults to false
              );
              // If successful (status 200), close modal
              if (result && result.status === 200) {
                setUpdatePricingOpen(false);
                setValidationErrors(null);
              }
            } catch (error) {
              // Error is already handled in handleUpdatePricingTiers
              console.error('Update failed:', error);
            }
          }}
          errors={validationErrors}
        />
      </Box>
    </Container>
  );
};

export default Pricing;
